import { ErrorHandler, inject, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isMbpError } from './mbp-error';
import { ClientErrorLogService } from './client-error-log.service';
import { ErrorMessageService } from './error-message.service';

@Service({ autoProvided: false })
export class GlobalErrorHandler implements ErrorHandler {
  private readonly errorLogger = inject(ClientErrorLogService);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly translate = inject(TranslateService);

  // After a new deployment the old container's hashed chunk files are gone, so a tab left
  // open (or restored from Safari's bfcache) fails to lazy-load a route with this error.
  // Guard by timestamp so a genuinely broken deploy doesn't reload in an infinite loop.
  private static readonly CHUNK_RELOAD_STORAGE_KEY = 'mbp-chunk-reload-at';
  private static readonly CHUNK_RELOAD_COOLDOWN_MS = 10_000;

  handleError(error: unknown): void {
    if (this.isChunkLoadError(error)) {
      console.warn('[GlobalErrorHandler] Stale chunk after a new deployment, reloading:', error);
      this.reloadForStaleChunk();
      return;
    }

    const mbpError = isMbpError(error) ? error : undefined;
    const isOriginalErrorHandled = isMbpError(mbpError?.originalError) && mbpError.originalError.handled === true;
    const isUnhandled = !mbpError?.handled && !isOriginalErrorHandled;

    // Opaque cross-origin "Script error." events carry no actionable detail
    // (WebKit strips it for security). iOS Safari fires one when the native
    // Share sheet opens, which produced a false global error toast. Suppress
    // only the user-facing message for these — still log them so genuine
    // cross-origin failures remain captured on the server.
    const isOpaqueCrossOriginError = this.isOpaqueCrossOriginError(error);

    if (isUnhandled && !isOpaqueCrossOriginError) {
      if (mbpError?.statusCode === 403) {
        void this.errorMessageService.displayErrorMessage(this.translate.instant('general.error'), this.translate.instant('errors.forbidden'));
      } else {
        void this.errorMessageService.displayGlobalErrorMessage();
      }
      console.error('[GlobalErrorHandler] Unhandled error:', error);
    }

    if (isUnhandled || mbpError?.logError === true) {
      void this.errorLogger.logErrorAsync(error);
    }
  }

  private isOpaqueCrossOriginError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
    return message.includes('Script error') || message.includes('An ErrorEvent with no error occurred');
  }

  // Matches the dynamic import failure messages thrown by Chrome, Safari and Firefox
  // when a lazy-loaded route's hashed chunk no longer exists on the server.
  private isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
    return /failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module/i.test(
      message
    );
  }

  private reloadForStaleChunk(): void {
    const lastReloadAt = Number(sessionStorage.getItem(GlobalErrorHandler.CHUNK_RELOAD_STORAGE_KEY) ?? 0);
    if (Date.now() - lastReloadAt < GlobalErrorHandler.CHUNK_RELOAD_COOLDOWN_MS) {
      return;
    }

    sessionStorage.setItem(GlobalErrorHandler.CHUNK_RELOAD_STORAGE_KEY, String(Date.now()));
    this.reload();
  }

  // Isolated so tests can spy on it without touching the non-configurable `window.location`.
  private reload(): void {
    window.location.reload();
  }
}
